// Code-action: "Quote the number" — Item C2.
//
// Wired to the Item-3e type-mismatch diagnostic. The ONLY type-mismatch the
// resolver flags that has a single, safe, mechanical fix is PostgreSQL's
// `text-vs-number` rule: a text column compared to a BARE numeric literal
// (`WHERE code = 5`, where `code` is text). PostgreSQL has no implicit
// text↔number cast, so this is a plan-time error; the fix that matches the
// user's evident intent is to quote the literal (`code = '5'`).
//
// The fix gates on the diagnostic being present at the cursor
// (`intellisense.type-mismatch` in `ctx.diagnosticCodes`) — the diagnostic has
// already vouched that this is a genuine mismatch, so the fix only has to find
// the bare numeric literal in the comparison and wrap it in single quotes. It
// never re-derives column types. The other two rules
// (`numeric-vs-nonnumeric-string`, `boolean-vs-nonboolean`) carry a STRING
// literal, not a bare number, so this fix finds nothing and stays silent.
//
// Insert-only at the number's bounds (no overwrite of surrounding bytes), so it
// composes with `emitSource`'s right-to-left patch invariant.

import {
  buildParsedDocumentFromSource,
  getDocumentStructure,
} from '../../../sql-grammar/document';
import { buildLineIndex, rangeFromOffsets } from '../../../sql-grammar/document/lineIndex';
import type { ParseOptions, SqlEngine } from '../../../sql-grammar/model';
import { TYPE_MISMATCH_DIAGNOSTIC_CODE } from '../../../intellisense/diagnostics/typeCheck';
import { databaseEngineIdToSqlEngine } from '../../../intellisense/versionGating/engineIdMapping';
import { extractQueryStructure } from '../../../sql-resolver/structure/extractQueryStructure';
import type {
  Comparison,
  OperandType,
  ParsedBuffer,
  QueryStructure,
} from '../../../sql-resolver/types';
import type {
  CodeActionContext,
  CodeActionEdit,
  CodeActionRegistryEntry,
} from '../../types';

const ENTRY_ID = 'quote-numeric-literal' as const;

export const quoteNumericLiteralEntry: CodeActionRegistryEntry = {
  id: ENTRY_ID,
  kind: 'quickfix',
  title: 'Quote the number',
  canApply: (ctx) => collectFix(ctx) !== null,
  buildEdits: (ctx) => {
    const fix = collectFix(ctx);
    return fix ? [fix] : [];
  },
};

// Bare numeric literal as written — `5`, `-3.14`, `2e3`. Excludes quoted forms.
const BARE_NUMBER_RE = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/;

function collectFix(ctx: CodeActionContext): CodeActionEdit | null {
  // Only when a type-mismatch diagnostic is present at the cursor.
  if (!ctx.diagnosticCodes.includes(TYPE_MISMATCH_DIAGNOSTIC_CODE)) return null;

  const source = ctx.sourceText;
  if (!source || source.trim().length === 0) return null;
  const sqlEngine = databaseEngineIdToSqlEngine(ctx.engineId);
  if (!sqlEngine) return null;

  const buffer = parseBuffer(source, sqlEngine);
  if (!buffer) return null;

  const cursor = ctx.cursor.offset;
  const stmt = buffer.documentStructure.statementAt(cursor)
    ?? buffer.documentStructure.statements[0]
    ?? null;
  if (!stmt) return null;

  let structure: QueryStructure;
  try {
    structure = extractQueryStructure(buffer, stmt);
  } catch {
    return null;
  }

  const comparison = findComparisonAtCursor(structure, cursor);
  if (!comparison) return null;

  const literal = bareNumericOperand(comparison.lhs) ?? (comparison.rhs ? bareNumericOperand(comparison.rhs) : null);
  if (!literal) return null;

  // Locate the bare number inside the comparison's source span. It is a
  // standalone token (negative look-around for identifier chars / quotes), so a
  // column name that happens to contain the same digits is never matched.
  const fragment = source.slice(comparison.range.startOffset, comparison.range.endOffset);
  const rel = findStandaloneNumber(fragment, literal);
  if (rel < 0) return null;

  const absStart = comparison.range.startOffset + rel;
  const absEnd = absStart + literal.length;
  const lineIdx = buildLineIndex(source);

  return {
    id: ENTRY_ID,
    description: `Quote as '${literal}'`,
    textEdits: [
      { range: rangeFromOffsets(lineIdx, absStart, absStart), newText: "'" },
      { range: rangeFromOffsets(lineIdx, absEnd, absEnd), newText: "'" },
    ],
  };
}

function bareNumericOperand(op: OperandType): string | null {
  if (op.kind !== 'literal') return null;
  if (op.sqlType !== 'integer' && op.sqlType !== 'numeric') return null;
  const text = op.text.trim();
  return BARE_NUMBER_RE.test(text) ? text : null;
}

// First standalone occurrence of `num` in `fragment`, or −1. "Standalone" =
// not adjacent to a word char, dot, or quote (so `col5`/`'5'`/`x.5` don't match).
function findStandaloneNumber(fragment: string, num: string): number {
  let from = 0;
  for (;;) {
    const idx = fragment.indexOf(num, from);
    if (idx < 0) return -1;
    const before = idx > 0 ? fragment[idx - 1]! : '';
    const after = idx + num.length < fragment.length ? fragment[idx + num.length]! : '';
    if (!isBoundaryBlocker(before) && !isBoundaryBlocker(after)) return idx;
    from = idx + 1;
  }
}

function isBoundaryBlocker(ch: string): boolean {
  return ch !== '' && (/[\w$.]/.test(ch) || ch === "'" || ch === '"' || ch === '`');
}

function findComparisonAtCursor(qs: QueryStructure, cursor: number): Comparison | null {
  for (const c of collectComparisons(qs)) {
    if (cursor >= c.range.startOffset && cursor <= c.range.endOffset) return c;
  }
  for (const nested of nestedStructures(qs)) {
    const hit = findComparisonAtCursor(nested, cursor);
    if (hit) return hit;
  }
  return null;
}

function collectComparisons(qs: QueryStructure): Comparison[] {
  const fragments = [
    ...(qs.where ?? []),
    ...(qs.having ?? []),
    ...(qs.mergeOn ?? []),
    ...(qs.onConflict?.where ?? []),
    ...(qs.joins ?? []).flatMap((j) => j.on ?? []),
  ];
  return fragments.map((f) => f.comparison).filter((c): c is Comparison => c !== undefined);
}

function nestedStructures(qs: QueryStructure): QueryStructure[] {
  const nested: QueryStructure[] = [];
  for (const fi of qs.fromItems ?? []) {
    if (fi.target.kind === 'derived-table') nested.push(fi.target.subquery);
  }
  for (const j of qs.joins ?? []) {
    if (j.right.target.kind === 'derived-table') nested.push(j.right.target.subquery);
  }
  for (const branch of qs.compoundBranches ?? []) nested.push(branch);
  return nested;
}

function parseBuffer(source: string, engine: SqlEngine): ParsedBuffer | null {
  try {
    const options: ParseOptions = { engine, identifierFolding: foldingForEngine(engine) };
    const parsedDocument = buildParsedDocumentFromSource(source, options);
    const documentStructure = getDocumentStructure(parsedDocument, 'quote-numeric-literal');
    return { parsedDocument, documentStructure };
  } catch {
    return null;
  }
}

function foldingForEngine(engine: SqlEngine): ParseOptions['identifierFolding'] {
  if (engine === 'postgres') return 'lower';
  if (engine === 'oracle') return 'upper';
  return 'case-insensitive';
}
